/**
 * Clinic chooser — shown when several live memberships exist and none is
 * selected. Ambiguity is answered by the person, never guessed (mirrors the
 * backend's x-clinic-id rule). Everything shown comes from the /me/authz
 * snapshot itself (ZOY-608 enrichment: displayName per membership) — no
 * per-clinic read, so no request ever leaves for a clinic not yet chosen.
 * A suspended membership and a deactivated clinic (ZOY-612) are named, not
 * openable.
 */

import { useIntl } from 'react-intl';
import s from '../access/access.module.css';
import { Button, Chip } from '@/ui/kit';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { clinicIsActive, type ClinicMembership } from '@/core/authz/api';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatDate } from '@/core/calendar/format';
import { EntryUrl } from '../entry/EntryUrl';

export function ClinicChooserPage({ memberships }: { memberships: readonly ClinicMembership[] }) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const { locale } = useLocale();

  return (
    <div className={s.page}>
      <div style={{ inlineSize: 'min(860px, 100%)' }}>
        <div className={s.pageHead} style={{ textAlign: 'center', marginInline: 'auto' }}>
          <h1 className={s.pageTitle}>{t('chooser.title')}</h1>
          <p className={s.pageSub}>{t('chooser.sub')}</p>
        </div>
        <div className={s.chooserGrid}>
          {memberships.map((m) => {
            const suspended = m.status !== 'active';
            const inactive = !clinicIsActive(m);
            return (
              <div key={m.clinicId} className={s.chooserCard}>
                <div className={s.chooserName} dir="auto">
                  {m.displayName?.trim() || t('chooser.unnamed')}
                </div>
                <div className={s.chooserMeta}>
                  {t('chooser.memberSince', {
                    date: formatDate(m.memberSince, {
                      locale,
                      calendar: 'jalali',
                      timezone: 'Asia/Tehran',
                    }),
                  })}
                </div>
                <EntryUrl slug={m.slug} />
                {inactive ? (
                  <Chip kind="warn">{t('chooser.inactive')}</Chip>
                ) : suspended ? (
                  <Chip kind="warn">{t('chooser.suspended')}</Chip>
                ) : (
                  <Button variant="brand" onClick={() => authz.selectClinic(m.clinicId)}>
                    {t('chooser.open')}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
